import {artifactMembershipNiceName} from '@wandb/weave/common/util/artifacts';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import * as Dialog from '@wandb/weave/components/Dialog';
import {Icon} from '@wandb/weave/components/Icon';
import React, {FC, useCallback, useContext, useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {AccountSelectorContext} from '../../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {envIsPublicCloud} from '../../../../config';
import {
  useArtifactIsProtectedQuery,
  useDeleteArtifactMutation,
  useUnlinkArtifactMutation,
} from '../../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {Analytics} from '../../../../services/analytics';
import {
  extractStatusCodeFromApolloError,
  propagateErrorsContext,
} from '../../../../util/errors';
import {useStatsigGateSourceArtifactProtection} from '../../../../util/rampFeatureFlags';
import {ARTIFACT_COLLECTION_OVERVIEW_QUERY} from '../../../ArtifactOverview/queries/overview.query';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../../../Banner';

const getErrorMessage = (error: unknown) => {
  const statusCode = extractStatusCodeFromApolloError(error);
  const message = (error as any)?.result?.errors[0]?.message;
  if (statusCode === 400 && message?.contains('existing alias')) {
    return 'This artifact cannot be deleted because of an existing alias.';
  }

  return 'Something went wrong while trying to delete this artifact.';
};

type DeleteMembershipModalProps = {
  open: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onClose: () => void;
  isPortfolio: boolean;
  artifactName: string;
  versionIndex: number;
  afterDelete: () => void;
  artifactId: string;
  artifactVersionId: string;
  artifactIsLinkedToGlobalRegistry?: boolean;
};

export const DeleteMembershipModal: FC<DeleteMembershipModalProps> = ({
  open,
  setIsOpen,
  onClose,
  isPortfolio,
  artifactName,
  versionIndex,
  afterDelete,
  artifactId,
  artifactVersionId,
  artifactIsLinkedToGlobalRegistry,
}) => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {data: isSourceArtifactProtectionEnabled} =
    useStatsigGateSourceArtifactProtection(orgName);

  const [isDeleting, setDeleting] = useState(false);
  const [deleteArtifact] = useDeleteArtifactMutation({
    context: propagateErrorsContext(),
    refetchQueries: [
      {
        query: ARTIFACT_COLLECTION_OVERVIEW_QUERY,
        variables: {artifactCollectionID: artifactId},
      },
    ],
    awaitRefetchQueries: true,
  });
  const [unlinkArtifact] = useUnlinkArtifactMutation({
    context: propagateErrorsContext(),
    refetchQueries: [
      {
        query: ARTIFACT_COLLECTION_OVERVIEW_QUERY,
        variables: {artifactCollectionID: artifactId},
      },
    ],
    awaitRefetchQueries: true,
  });

  const {data: artifactIsProtectedData} = useArtifactIsProtectedQuery({
    variables: {artifactID: artifactVersionId},
    skip:
      !artifactIsLinkedToGlobalRegistry ||
      isPortfolio ||
      !isSourceArtifactProtectionEnabled,
  });
  const artifactIsProtected = useMemo(() => {
    const artifactIsProtected =
      artifactIsProtectedData?.artifact?.isProtected ?? false;
    if (artifactIsProtected) {
      Analytics.track('source_artifact_protection', {
        location: 'DeleteMembershipModal',
        entityName:
          artifactIsProtectedData?.artifact?.artifactSequence?.project?.entity
            ?.name ?? '',
        projectName:
          artifactIsProtectedData?.artifact?.artifactSequence?.project?.name ??
          '',
        artifactCollectionName: artifactName,
      });
    }
    return artifactIsProtected;
  }, [artifactIsProtectedData, artifactName]);

  const onDeleteClick = useCallback(async () => {
    setDeleting(true);

    try {
      if (isPortfolio) {
        await unlinkArtifact({
          variables: {
            artifactPortfolioID: artifactId ?? '',
            artifactID: artifactVersionId,
          },
        });
      } else {
        await deleteArtifact({
          variables: {
            artifactID: artifactVersionId,
            deleteAliases: true,
          },
          context: propagateErrorsContext(),
        });
      }
      toast(
        `Artifact ${artifactName}:v${versionIndex} ${
          isPortfolio ? 'unlinked' : 'deleted'
        }.`,
        {type: 'success'}
      );
      afterDelete();
    } catch (error) {
      console.error(error);
      if (isPortfolio && extractStatusCodeFromApolloError(error) === 403) {
        toast(
          'You must be a registry admin to unlink a version with protected aliases.'
        );
      } else {
        toast(getErrorMessage(error));
      }
      onClose();
    }
    setDeleting(false);
  }, [
    afterDelete,
    artifactId,
    artifactVersionId,
    deleteArtifact,
    isPortfolio,
    unlinkArtifact,
    onClose,
    artifactName,
    versionIndex,
  ]);

  return (
    <Dialog.Root open={open} onOpenChange={setIsOpen}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          className="w-[600px] rounded-lg"
          onInteractOutside={e => {
            e.preventDefault();
          }}>
          <Dialog.Title>
            {isPortfolio ? 'Unlink' : 'Delete'} this artifact version?
          </Dialog.Title>
          <Dialog.Description>
            {artifactIsProtected && (
              <Banner variant="error" className="mt-16">
                <BannerFlexWrapper>
                  <div>
                    <Icon name="warning" />
                  </div>
                  <BannerTextWrapper>
                    You are unable to delete this artifact version until it has
                    been unlinked from the registry collections it is protected
                    in.
                  </BannerTextWrapper>
                </BannerFlexWrapper>
              </Banner>
            )}
          </Dialog.Description>
          <Dialog.Description className="pt-16" data-test="delete-summary">
            {isPortfolio
              ? 'Any references to this linked version will be broken.'
              : `Artifact "${artifactMembershipNiceName(artifactName, {
                  versionIndex,
                })}" will be scheduled for deletion. Any references to this artifact will be broken.
                Registry content linked to this artifact version will no longer exist.`}
          </Dialog.Description>
          <div className="mb-8 mt-24 flex items-center">
            <Button
              data-cy="artifact-delete-confirm"
              variant="destructive"
              disabled={isDeleting || artifactIsProtected}
              size="large"
              onClick={onDeleteClick}>
              Delete
            </Button>
            <Dialog.Close className="ml-8" asChild>
              <Button
                className="dark:text-moon-200/100 dark:filter-none hover:dark:text-moon-200"
                variant="ghost"
                size="large"
                disabled={isDeleting}>
                Cancel
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
